import { call, put, takeEvery, select } from 'redux-saga/effects';
import { quotesApi } from '../../api/quotes-api';
import { Quote, QuoteFilters, QuoteMetrics } from '../../types/quote.types';
import {
  setLoading,
  setError,
  setQuotes,
  setMetrics,
  setPagination,
  updateQuote,
  removeQuote,
  addQuote,
} from '../slices/quotesSlice';

// Types for saga actions
interface FetchQuotesAction {
  type: string;
  payload?: Partial<QuoteFilters>;
}

interface CreateQuoteAction {
  type: string;
  payload: any; // CreateQuoteRequest
}

interface UpdateQuoteAction {
  type: string;
  payload: { id: string; data: any };
}

interface DeleteQuoteAction {
  type: string;
  payload: string; // quoteId
}

interface SendQuoteAction {
  type: string;
  payload: string; // quoteId
}

interface AcceptQuoteAction {
  type: string;
  payload: string; // quoteId
}

interface RejectQuoteAction {
  type: string;
  payload: { id: string; reason: string };
}

interface DuplicateQuoteAction {
  type: string;
  payload: string; // quoteId
}

// Action types
export const QUOTES_ACTIONS = {
  FETCH_QUOTES: 'quotes/fetchQuotes',
  FETCH_STATS: 'quotes/fetchStats',
  CREATE_QUOTE: 'quotes/createQuote',
  UPDATE_QUOTE: 'quotes/updateQuote',
  DELETE_QUOTE: 'quotes/deleteQuote',
  SEND_QUOTE: 'quotes/sendQuote',
  ACCEPT_QUOTE: 'quotes/acceptQuote',
  REJECT_QUOTE: 'quotes/rejectQuote',
  DUPLICATE_QUOTE: 'quotes/duplicateQuote',
} as const;

// Fetch quotes saga
function* fetchQuotesSaga(action: FetchQuotesAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const state = yield select();
    const filters = action.payload || state.quotes.filters;

    const response = yield call(quotesApi.getMyQuotes, filters);
    
    yield put(setQuotes(response.quotes || []));
    yield put(setPagination({
      totalItems: response.total || 0,
      totalPages: response.totalPages || 0,
    }));

  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al cargar las cotizaciones';
    yield put(setError(errorMessage));
    yield put(setQuotes([]));
    yield put(setPagination({ totalItems: 0, totalPages: 0 }));
  } finally {
    yield put(setLoading(false));
  }
}

// Fetch stats saga
function* fetchStatsSaga() {
  try {
    const metrics: QuoteMetrics = yield call(quotesApi.getStats);
    yield put(setMetrics(metrics));
  } catch (error: any) {
    // Don't set error for stats, just log it
  }
}

// Create quote saga
function* createQuoteSaga(action: CreateQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const newQuote: Quote = yield call(quotesApi.create, action.payload);
    yield put(addQuote(newQuote));
    
    // Refresh stats after creating
    yield call(fetchStatsSaga);

    return { success: true, data: newQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al crear la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Update quote saga
function* updateQuoteSaga(action: UpdateQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedQuote: Quote = yield call(quotesApi.update, action.payload.id, action.payload.data);
    yield put(updateQuote(updatedQuote));
    
    // Refresh stats after updating
    yield call(fetchStatsSaga);

    return { success: true, data: updatedQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al actualizar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Delete quote saga
function* deleteQuoteSaga(action: DeleteQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    yield call(quotesApi.delete, action.payload);
    yield put(removeQuote(action.payload));
    
    // Refresh stats after deleting
    yield call(fetchStatsSaga);

    return { success: true };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al eliminar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Send quote saga
function* sendQuoteSaga(action: SendQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedQuote: Quote = yield call(quotesApi.send, action.payload);
    yield put(updateQuote(updatedQuote));
    
    // Refresh stats after sending
    yield call(fetchStatsSaga);

    return { success: true, data: updatedQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al enviar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Accept quote saga
function* acceptQuoteSaga(action: AcceptQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedQuote: Quote = yield call(quotesApi.accept, action.payload);
    yield put(updateQuote(updatedQuote));
    
    // Refresh stats after accepting
    yield call(fetchStatsSaga);

    return { success: true, data: updatedQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al aceptar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Reject quote saga
function* rejectQuoteSaga(action: RejectQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const updatedQuote: Quote = yield call(quotesApi.reject, action.payload.id, action.payload.reason);
    yield put(updateQuote(updatedQuote));
    
    // Refresh stats after rejecting
    yield call(fetchStatsSaga);

    return { success: true, data: updatedQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al rechazar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Duplicate quote saga
function* duplicateQuoteSaga(action: DuplicateQuoteAction) {
  try {
    yield put(setLoading(true));
    yield put(setError(null));

    const duplicatedQuote: Quote = yield call(quotesApi.duplicate, action.payload);
    yield put(addQuote(duplicatedQuote));
    
    // Refresh stats after duplicating
    yield call(fetchStatsSaga);

    return { success: true, data: duplicatedQuote };
  } catch (error: any) {
    const errorMessage = error.response?.data?.message || 'Error al duplicar la cotización';
    yield put(setError(errorMessage));
    return { success: false, error: errorMessage };
  } finally {
    yield put(setLoading(false));
  }
}

// Root quotes saga
export function* quotesSaga() {
  yield takeEvery(QUOTES_ACTIONS.FETCH_QUOTES, fetchQuotesSaga);
  yield takeEvery(QUOTES_ACTIONS.FETCH_STATS, fetchStatsSaga);
  yield takeEvery(QUOTES_ACTIONS.CREATE_QUOTE, createQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.UPDATE_QUOTE, updateQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.DELETE_QUOTE, deleteQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.SEND_QUOTE, sendQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.ACCEPT_QUOTE, acceptQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.REJECT_QUOTE, rejectQuoteSaga);
  yield takeEvery(QUOTES_ACTIONS.DUPLICATE_QUOTE, duplicateQuoteSaga);
}

export default quotesSaga;



